import { useEffect, useRef, useState } from "react";
import { Search, ChevronDown, X } from "lucide-react";
import { filterInsurances } from "./insurances";

/**
 * Searchable insurance combobox.
 *
 * Props:
 *  - mode: "rsv" → emits selection.nr (AdressNummer) as the primary value.
 *          "haftpflicht" → emits selection.name only.
 *  - value: current text (display)
 *  - selectedNr / selectedName: full selection state (so we can show "1858 — Debeka …")
 *  - onSelect: ({ nr, name }) => void   (cleared with null)
 *  - idPrefix: testid prefix
 *  - placeholder
 */
export const InsuranceSelect = ({
  mode = "rsv",
  selectedNr = "",
  selectedName = "",
  onSelect,
  idPrefix = "ins",
  placeholder = "Versicherung suchen (Name)",
}) => {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState([]);
  const boxRef = useRef(null);

  const hasSelection = Boolean(selectedNr || selectedName);

  useEffect(() => {
    const onDoc = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  useEffect(() => {
    if (!open) return;
    setResults(filterInsurances(q, 40, mode));
  }, [q, open, mode]);

  const pick = (item) => {
    onSelect({ nr: item.nr, name: item.name });
    setQ("");
    setOpen(false);
  };

  const clear = () => {
    onSelect(null);
    setQ("");
    setOpen(false);
  };

  return (
    <div ref={boxRef} className="relative">
      {hasSelection ? (
        <div
          data-testid={`${idPrefix}-selected`}
          className="flex items-center justify-between gap-3 bg-white border border-[#0a1128] rounded-sm px-4 py-3"
        >
          <div className="min-w-0">
            <div className="text-[14px] font-semibold text-[#0a1128] truncate">
              {selectedName || "—"}
            </div>
          </div>
          <button
            type="button"
            data-testid={`${idPrefix}-clear`}
            onClick={clear}
            className="shrink-0 w-8 h-8 inline-flex items-center justify-center rounded-full text-[#505a74] hover:bg-[#f4f3ed] hover:text-[#0a1128] transition"
            aria-label="Auswahl entfernen"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      ) : (
        <>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#505a74]" />
            <input
              type="text"
              data-testid={`${idPrefix}-search`}
              value={q}
              onChange={(e) => {
                setQ(e.target.value);
                setOpen(true);
              }}
              onFocus={() => setOpen(true)}
              placeholder={placeholder}
              className="block w-full bg-white border border-[#e8e6df] rounded-sm pl-10 pr-10 py-3 text-[15px] focus:outline-none focus:border-[#0a1128] transition"
            />
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#505a74]" />
          </div>
          {open && (
            <div
              data-testid={`${idPrefix}-results`}
              className="absolute z-30 left-0 right-0 mt-1 bg-white border border-[#e8e6df] rounded-sm shadow-lg max-h-80 overflow-auto"
            >
              {results.length === 0 ? (
                <div className="px-4 py-3 text-[13px] text-[#505a74]">
                  Keine Treffer — Suchbegriff anpassen.
                </div>
              ) : (
                results.map((r) => (
                  <button
                    type="button"
                    key={r.nr}
                    data-testid={`${idPrefix}-result-${r.nr}`}
                    onClick={() => pick(r)}
                    className="block w-full text-left px-4 py-2.5 border-b border-[#e8e6df] last:border-b-0 hover:bg-[#fafaf7] transition"
                  >
                    <div className="flex items-baseline gap-3">
                      <span className="text-[14px] text-[#0a1128] truncate">
                        {r.name}
                      </span>
                    </div>
                  </button>
                ))
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};
